import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PatternFormat } from "react-number-format";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../contexts/Auth";
import Footer from "../components/Footer";
import enqueueSnackbar from "../helpers/enqueueSnackbar";
import authSchema from "../schemas/auth";
import logohorizontal from "../assets/logohorizontal.png";
import fyndmockup from "../assets/fyndmockup.png";
import qrcodeDownload from "../assets/qrcode.png";
import appleDownloadBlack from "../assets/apple-download-black.png";
import googleDownloadBlack from "../assets/google-download-black.png";

type Values = {
  cpf: string;
  password: string;
};

const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexFlow: "row nowrap",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
}));

const StyledButton = styled(Button)({
  position: "relative",

  "& .StyledButton-CircularProgress": {
    position: "absolute",
    height: "100%",
    width: "100%",
    left: 0,
    top: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

function Login() {
  const navigate = useNavigate();
  const { token, login } = useAuth();

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useForm<Values>({
    resolver: yupResolver(authSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);

  async function submit(values: Values): Promise<void> {
    try {
      await login(values.cpf, values.password);
    } catch (error) {
      enqueueSnackbar(error?.message, "error");

      setError("root", { message: error.message });
    }
  }

  useEffect(() => {
    if (token) {
      navigate("/app/minha-conta", { replace: true });
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexFlow: { xs: "column nowrap", md: "row nowrap" },
          justifyContent: { xs: "start", md: "space-evenly" },
          alignItems: { xs: "center", md: "center" },
          minHeight: "100vh",
          width: "100vw",
        }}
      >
        <Stack
          spacing={4}
          justifyContent="start"
          alignItems="center"
          sx={(theme) => ({
            width: "100%",
            maxWidth: { md: "50%", lg: "30%" },
            padding: { xs: theme.spacing(4), md: 0 },
          })}
        >
          <img src={logohorizontal} style={{ width: "100px" }} />

          <Typography sx={{ fontWeight: 500 }}>Área do Cliente</Typography>

          <StyledForm onSubmit={handleSubmit(submit)}>
            <Stack
              spacing={2}
              sx={(theme) => ({
                width: "100%",
                maxWidth: theme.spacing(50),
              })}
            >
              <Controller
                control={control}
                name="cpf"
                render={({
                  field: { onBlur, value, name },
                  fieldState: { error },
                }) => (
                  <PatternFormat
                    type="text"
                    label="CPF"
                    name={name}
                    value={value || ""}
                    onBlur={onBlur}
                    onValueChange={({ value }) =>
                      setValue(name, value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      })
                    }
                    error={!!error}
                    helperText={!!error ? error.message : undefined}
                    placeholder="000.000.000-00"
                    format="###.###.###-##"
                    mask="_"
                    valueIsNumericString
                    customInput={TextField}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />

              <Controller
                control={control}
                name="password"
                render={({
                  field: { onBlur, onChange, value, name },
                  fieldState: { error },
                }) => (
                  <TextField
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    label="Senha"
                    placeholder="Senha"
                    name={name}
                    value={value || ""}
                    onBlur={onBlur}
                    onChange={onChange}
                    error={!!error}
                    helperText={!!error ? error.message : undefined}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      notched: true,
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          style={{ cursor: "Pointer", opacity: "0.7" }}
                        >
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    required
                  />
                )}
              />

              <StyledButton
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting || isSubmitSuccessful}
                type="submit"
              >
                Entrar
                {isSubmitting && (
                  <div className="StyledButton-CircularProgress">
                    <CircularProgress />
                  </div>
                )}
              </StyledButton>
            </Stack>
          </StyledForm>

          <Stack spacing={2} alignItems="center">
            <span>
              Baixe o nosso aplicativo <b>Fynd Saúde</b>!
            </span>

            <Stack direction="row" spacing={2}>
              <img src={qrcodeDownload} style={{ height: 135 }} />

              <Stack
                justifyContent="space-between"
                alignItems="center"
                sx={{ py: "4px" }}
              >
                <a
                  href="https://play.google.com/store/apps/details?id=br.com.conexo.saude"
                  target="_blank"
                  style={{ display: "inline-block", height: 55 }}
                >
                  <img src={googleDownloadBlack} style={{ height: 55 }} />
                </a>

                <a
                  href="https://apps.apple.com/br/app/conexo-saúde/id1542610505?platform=iphone"
                  target="_blank"
                  style={{ display: "inline-block", height: 55 }}
                >
                  <img src={appleDownloadBlack} style={{ height: 55 }} />
                </a>
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            maxWidth: { md: "50%", lg: "30%" },
          }}
        >
          <img src={fyndmockup} style={{ width: "100%", maxWidth: "535px" }} />
        </Box>
      </Box>

      <Footer />
    </>
  );
}

export default Login;
